<template>
    <div
        class="l-ss-c-input-container"
        :class="{
            'l-ss-c-input-container-error': error,
            'l-ss-c-input-container-focus': isFocus
        }">
        <div
            class="l-ss-c-input-wrapper"
            @click.self="focus">
            <div
                class="l-ss-c-input-prefix"
                v-if="$slots.prefix">
                <slot name="prefix"></slot>
            </div>

            <input
                v-if="!hasContentSlot"
                class="l-ss-c-input"
                v-model="inputValue"
                :placeholder="animatedPlaceholder"
                :autocomplete="autocomplete"
                :autofocus="autofocus"
                :disabled="loading"
                :type="type"
                @focus="onFocus"
                @click="$emit('click', $event)"
                @blur="onBlur"
                @input="$emit('native-input', $event)"
                @keydown="$emit('keydown', $event)"
                @mousedown="$emit('mousedown', $event)"
                ref="input">
            <div
                class="l-ss-c-input-custom-content"
                v-if="hasContentSlot">
                <slot name="content"></slot>
            </div>

            <div class="l-ss-c-input-actions">
                <button
                    v-if="allowClear"
                    class="l-ss-c-input-clear"
                    :class="{ 'l-ss-c-input-clear-with-border': hasSuffix }">
                    <i
                        @click="$emit('clear', $event)"
                        v-html="deleteIcon"></i>
                </button>

                <slot name="suffix"></slot>

                <app-btn
                    class="l-ss-c-input-search"
                    v-if="showSearchIcon"
                    :loading="loading"
                    :disabled="disabledSearch"
                    @click="$emit('search', $event)">
                    <i
                        v-html="searchIcon"
                        v-if="!loading"></i>
                </app-btn>
            </div>
        </div>

        <div
            v-if="error"
            class="l-ss-c-input-error-message">
            {{ error }}
        </div>
    </div>
</template>

<script>
    import deleteIcon from '../../assets/images/icons/clear-search-icon.svg';
    import searchIcon from '@/core/assets/images/icons/search-icon.svg';
    import AppBtn from '@/core/components/common/app-btn.vue';

    const TYPING_SPEED = 100;
    const DELETING_SPEED = 50;
    const PAUSE_DURATION = 2000;

    export default {
        name: 'app-input',
        components: { AppBtn },
        props: {
            value: {
                type: String,
            },
            error: {
                type: String,
            },
            placeholder: {
                type: String,
                default: '',
            },
            showSearchIcon: {
                type: Boolean,
                default: false,
            },
            clearable: {
                type: Boolean,
                default: false,
            },
            type: {
                type: String,
            },
            autocomplete: {
                type: String,
            },
            autofocus: {
                type: Boolean,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            disabledSearch: {
                type: Boolean,
                default: false,
            },
            hasAnimatedPlaceholder: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                isFocus: false,
                currentPlaceholderIndex: 0,
                currentPlaceholderText: '',
                isTyping: false,
                isDeleting: false,
                currentCharIndex: 0,
            };
        },
        computed: {
            inputValue: {
                get() {
                    return this.value;
                },
                set(value) {
                    this.$emit('input', value);
                },
            },
            searchIcon() {
                return searchIcon;
            },
            deleteIcon() {
                return deleteIcon;
            },
            allowClear() {
                return this.clearable && !!this.inputValue;
            },
            hasSuffix() {
                return !!this.$slots?.suffix;
            },
            hasContentSlot() {
                return !!this.$slots.content;
            },
            animatedPlaceholder() {
                return this.isAnimatedPlaceholderActive ?
                    this.currentPlaceholderText :
                    this.placeholder;
            },
            placeholderTexts() {
                const original = this.$configuration.inputPlaceholder.placeholderTexts;
                const shuffled = original.slice();

                for (let i = shuffled.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                }
                return shuffled;
            },
            isAnimatedPlaceholderActive() {
                return this.$configuration.inputPlaceholder.isAnimated && this.hasAnimatedPlaceholder;
            },
        },
        mounted() {
            if (this.isAnimatedPlaceholderActive) {
                this.startTypingAnimation();
            }
        },
        methods: {
            focus() {
                if (this.$refs.input) {
                    this.$refs.input.focus();
                }
            },
            blur() {
                if (this.$refs.input) {
                    this.$refs.input.blur();
                }
            },
            onFocus(event) {
                this.isFocus = true;
                this.$emit('focus', event);
            },
            onBlur(event) {
                this.isFocus = false;
                this.$emit('blur', event);
            },
            startTypingAnimation() {
                if (this.isTyping || this.isDeleting) return;
                
                const currentText = this.placeholderTexts[this.currentPlaceholderIndex];
                
                if (this.currentCharIndex < currentText.length) {
                    this.isTyping = true;
                    this.currentPlaceholderText = currentText.substring(0, this.currentCharIndex + 1);
                    this.currentCharIndex++;
                    setTimeout(() => {
                        this.isTyping = false;
                        this.startTypingAnimation();
                    }, TYPING_SPEED);
                } else {
                    setTimeout(() => {
                        this.isDeleting = true;
                        this.startDeletingAnimation();
                    }, PAUSE_DURATION);
                }
            },
            startDeletingAnimation() {
                if (this.currentCharIndex > 0) {
                    const currentText = this.placeholderTexts[this.currentPlaceholderIndex];
                    this.currentPlaceholderText = currentText.substring(0, this.currentCharIndex - 1);
                    this.currentCharIndex--;
                    setTimeout(() => {
                        this.startDeletingAnimation();
                    }, DELETING_SPEED);
                } else {
                    this.isDeleting = false;
                    this.currentPlaceholderIndex = (this.currentPlaceholderIndex + 1) % this.placeholderTexts.length;
                    this.startTypingAnimation();
                }
            },
        },
    };
</script>
